import { useCallback, useSyncExternalStore } from 'react';
import { useClerk } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';

import { clearLastActivity, writeLastActivity } from '@/components/me/mePortal';

export type MeSignOutReason = 'button' | 'idle';

// One sign-out per tab, shared by every useMeSignOut instance: the header
// button, the idle check and the boot gate see the same guard and the same
// `isSigningOut`, so the button is disabled during an idle sign-out too.
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function setInFlight(next: Promise<void> | null): void {
  inFlight = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True while a /me sign-out of this tab is running (or succeeded and the signed-out tree has not mounted yet). */
export function isMeSigningOut(): boolean {
  return inFlight !== null;
}

/**
 * Forgets a finished sign-out. Called when the signed-out tree mounts (App.tsx)
 * so a later sign-in in this tab starts clean, and by tests between cases.
 */
export function resetMeSignOut(): void {
  if (inFlight !== null) setInFlight(null);
}

/**
 * Sign-out of the /me portal (#1768): running queries are cancelled and the
 * query cache is cleared FIRST, then the activity marker, then the Clerk
 * session ends. Nothing personal stays in the tab's memory or storage on a
 * private device. A second call while one is running is ignored.
 *
 * When Clerk rejects (offline), an EXPIRED marker is written back before the
 * error is rethrown: the session is still alive, and the next boot of any tab
 * must still end it rather than lose the idle evidence.
 */
export function useMeSignOut() {
  const clerk = useClerk();
  const queryClient = useQueryClient();
  const isSigningOut = useSyncExternalStore(subscribe, isMeSigningOut);

  const signOut = useCallback(
    async (_reason: MeSignOutReason) => {
      if (inFlight !== null) return;
      const run = (async () => {
        await queryClient.cancelQueries();
        queryClient.clear();
        clearLastActivity();
        try {
          await clerk.signOut({ redirectUrl: '/' });
        } catch (error) {
          writeLastActivity(0);
          throw error;
        }
      })();
      setInFlight(run);
      try {
        await run;
      } catch (error) {
        setInFlight(null);
        throw error;
      }
    },
    [clerk, queryClient],
  );

  return { signOut, isSigningOut };
}
